/**
 * Login Page Placeholder
 * Login Page with validation, error handling, and API integration
 * 
 * ARCHITECTURAL STANDARDS:
 * - Uses useAPICall hook for API requests
 * - Centralized API config (API_CONFIG.auth.login)
 * - Toast notifications for errors
 * - Client-side validation before API call
 * - Accessible form with ARIA labels
 */

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  FormControlLabel,
  InputAdornment,
  IconButton,
  LinearProgress,
  Link,
  Typography,
  Chip,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { toast } from 'react-toastify';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import LockIcon from '@mui/icons-material/Lock';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import SecurityOutlinedIcon from '@mui/icons-material/SecurityOutlined';
import VerifiedUserOutlinedIcon from '@mui/icons-material/VerifiedUserOutlined';

import useAPICall from '../../hooks/useAPICall';
import apiClient from '../../services/apiClient';
import { API_CONFIG } from '../../config/api.config';
import { ENV, resolveBrowserLogoPath } from '../../config/environment';
import LabeledField from '../../components/common/LabeledField';

/**
 * Error message constants
 */
const ERROR_MESSAGES = {
  USERNAME_REQUIRED: 'Username or email is required',
  USERNAME_MIN_LENGTH: 'Username must be at least 3 characters',
  PASSWORD_REQUIRED: 'Password is required',
  PASSWORD_MIN_LENGTH: 'Password must be at least 6 characters',
  LOGIN_FAILED: 'Invalid username or password. Please try again.',
  NETWORK_ERROR: 'Network error. Please check your connection and try again.',
};

/**
 * Form validation function
 * @param {Object} credentials - Form data
 * @returns {Object} Validation errors
 */
const validateLoginForm = (credentials) => {
  const errors = {};

  // Username validation
  if (!credentials.username || !credentials.username.trim()) {
    errors.username = ERROR_MESSAGES.USERNAME_REQUIRED;
  } else if (credentials.username.trim().length < 3) {
    errors.username = ERROR_MESSAGES.USERNAME_MIN_LENGTH;
  }

  // Password validation
  if (!credentials.password) {
    errors.password = ERROR_MESSAGES.PASSWORD_REQUIRED;
  } else if (credentials.password.length < 6) {
    errors.password = ERROR_MESSAGES.PASSWORD_MIN_LENGTH;
  }

  return errors;
};

const hasUserInfo = (userInfo) => (
  userInfo &&
  typeof userInfo === 'object' &&
  !Array.isArray(userInfo) &&
  Boolean(userInfo.group) &&
  Boolean(userInfo.name) &&
  Boolean(userInfo.username)
);

const getRedirectPath = (from) => {
  if (!from) {
    return '/search/hcp';
  }

  return `${from.pathname || '/search/hcp'}${from.search || ''}${from.hash || ''}`;
};

const LoginPage = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const usernameRef = useRef(null);
  const logoSrc = resolveBrowserLogoPath();
  const browserClientName = ENV.BROWSER_CLIENT_NAME || 'MDM';
  const allowOfflineLogin = ENV.ENABLE_OFFLINE_LOGIN;
  
  const { loading, execute } = useAPICall();
  
  const [credentials, setCredentials] = useState({
    username: '',
    password: '',
  });
  const [validationErrors, setValidationErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);

  // Focus username field on mount
  useEffect(() => {
    if (usernameRef.current) {
      usernameRef.current.focus();
    }
  }, []);

  // Auto login check when user redirects or visits with specific parameters (e.g. from AceCore UI / mdm-react / bleubird)
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const source = urlParams.get('source') || '';
    const encodedUsername = urlParams.get('username') || '';
    const encodedPassword = urlParams.get('password') || '';

    const searchStr = (window.location.search + (location.state?.from?.search || '')).toLowerCase();
    const shouldAutoLogin =
      source === 'bleubird' ||
      source === 'acecore' ||
      source === 'mdm-react' ||
      searchStr.includes('autologin') ||
      searchStr.includes('acecore') ||
      searchStr.includes('bleubird') ||
      searchStr.includes('mdm-react') ||
      searchStr.includes('source=mdm');

    if (shouldAutoLogin) {
      // Decode credentials from URL if present, otherwise use defaults
      let resolvedUsername = process.env.REACT_APP_AUTO_LOGIN_USERNAME || '';
      let resolvedPassword = process.env.REACT_APP_AUTO_LOGIN_PASSWORD || '';

      if (encodedUsername) {
        try { resolvedUsername = atob(encodedUsername); } catch (e) { /* use default */ }
      }
      if (encodedPassword) {
        try { resolvedPassword = atob(encodedPassword); } catch (e) { /* use default */ }
      }

      const autoCredentials = {
        username: resolvedUsername,
        password: resolvedPassword,
      };

      setCredentials(autoCredentials);

      const performAutoLogin = async () => {
        try {
          // Call login API
          await execute(
            API_CONFIG.auth.login,
            autoCredentials,
            {
              errorMessage: ERROR_MESSAGES.LOGIN_FAILED,
              retries: 0,
            }
          );
          
          // Fetch authenticated user info
          const userInfoResponse = await apiClient.get(API_CONFIG.auth.getUserInfo.endpoint);
          const userInfo = userInfoResponse?.data || {};
          if (!hasUserInfo(userInfo)) {
            throw new Error('User info not available');
          }

          const permissions = userInfo?.group === 'rw' ? ['user', 'admin'] : ['user'];
          const hydratedUser = {
            ...userInfo,
            role: permissions.includes('admin') ? 'admin' : 'user',
            permissions,
          };

          // Complete login and store session
          sessionStorage.setItem('authToken', 'session');
          sessionStorage.setItem('user', JSON.stringify(hydratedUser));

          const from = getRedirectPath(location.state?.from);
          navigate(from, { replace: true });
        } catch (err) {
          console.error('Auto-login error:', err);
          
          if (allowOfflineLogin && !err.response) {
            const offlineUser = {
              id: 'offline-user',
              username: resolvedUsername,
              name: 'Demo Admin User',
              role: 'admin',
              permissions: ['user', 'admin'],
              offlineMode: true,
            };
            sessionStorage.setItem('authToken', 'session');
            sessionStorage.setItem('user', JSON.stringify(offlineUser));
            const from = getRedirectPath(location.state?.from);
            navigate(from, { replace: true });
            // toast.success('Entered offline mode via auto-login');
          } else {
            toast.error('Auto-login failed. Please sign in manually.');
          }
        }
      };

      performAutoLogin();
    }
  }, [navigate, location.state, execute, allowOfflineLogin]);

  /**
   * Handle input changes
   */
  const handleChange = (field) => (event) => {
    setCredentials({
      ...credentials,
      [field]: event.target.value,
    });
    // Clear validation error for this field
    if (validationErrors[field]) {
      setValidationErrors({
        ...validationErrors,
        [field]: null,
      });
    }
  };

  /**
   * Toggle password visibility
   */
  const handleTogglePassword = () => {
    setShowPassword(!showPassword);
  };

  const completeLogin = (user) => {
    sessionStorage.setItem('authToken', 'session');
    sessionStorage.setItem('user', JSON.stringify(user));

    if (rememberMe) {
      localStorage.setItem('mdm_remember_user', credentials.username || user.username || 'offline-user');
    } else {
      localStorage.removeItem('mdm_remember_user');
    }

    const from = getRedirectPath(location.state?.from);
    navigate(from, { replace: true });
  };

  const handleOfflineAccess = () => {
    const offlineUser = {
      id: 'offline-user',
      username: credentials.username || 'offline@local',
      name: 'Offline User',
      role: 'admin',
      permissions: ['user', 'admin'],
      offlineMode: true,
    };
    completeLogin(offlineUser);
    // toast.success('Entered offline mode');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Client-side validation
    const errors = validateLoginForm(credentials);
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      
      // Announce error to screen readers
      const errorMessage = Object.values(errors).join('. ');
      toast.error(errorMessage, { autoClose: 5000 });
      return;
    }
    
    try {
      // Call login API using centralized config
      await execute(
        API_CONFIG.auth.login,
        credentials,
        {
          errorMessage: ERROR_MESSAGES.LOGIN_FAILED,
          retries: 0, // No retry for auth failures
        }
      );
      const userInfoResponse = await apiClient.get(API_CONFIG.auth.getUserInfo.endpoint);
      const userInfo = userInfoResponse?.data || {};
      if (!hasUserInfo(userInfo)) {
        throw new Error('User info not available');
      }

      const permissions = userInfo?.group === 'rw' ? ['user', 'admin'] : ['user'];
      const hydratedUser = {
        ...userInfo,
        role: permissions.includes('admin') ? 'admin' : 'user',
        permissions,
      };

      completeLogin(hydratedUser);
      // toast.success('Login successful!');

    } catch (err) {
      // Error already handled by useAPICall hook
      console.error('Login error:', err);

      if (allowOfflineLogin && !err.response) {
        handleOfflineAccess();
        // toast.info('Backend unavailable. Continuing in offline mode.');
        return;
      }
      
      // Additional user-friendly error handling
      if (err.message?.includes('Network')) {
        toast.error(ERROR_MESSAGES.NETWORK_ERROR);
      }
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        backgroundColor: '#f1f5f9',
        px: 2,
      }}
    >
      <Card
        sx={{
          maxWidth: 440,
          width: '100%',
          backgroundColor: '#ffffff',
          borderRadius: '24px',
          boxShadow: '0 10px 40px -10px rgba(0,0,0,0.08)',
          overflow: 'visible',
        }}
      >
        <CardContent sx={{ p: { xs: 4, sm: 5 }, pb: { xs: 5, sm: 6 }, position: 'relative' }}>
          {loading && (
            <LinearProgress 
              sx={{ 
                position: 'absolute', 
                top: 0, 
                left: 0, 
                right: 0,
                borderTopLeftRadius: '24px',
                borderTopRightRadius: '24px',
              }} 
            />
          )}

          {/* Client Logo */}
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
            {logoSrc ? (
              <Box
                component="img"
                src={logoSrc}
                alt={`${browserClientName} logo`}
                sx={{
                  height: 48,
                  width: 'auto',
                  objectFit: 'contain',
                }}
              />
            ) : (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box
                  sx={{
                    backgroundColor: theme.palette.primary.light,
                    borderRadius: '50%',
                    p: 1,
                  }}
                >
                  <LockIcon sx={{  color: theme.palette.primary.main }} />
                </Box>
                <Typography variant="h5" sx={{ fontWeight: 600, color: '#1e293b' }}>
                  {browserClientName}
                </Typography>
              </Box>
            )}
          </Box>
          
          <Typography variant="h4" gutterBottom sx={{ textAlign: 'center', mb: 1, fontWeight: 700, color: '#1e293b',  }}>
            Welcome back
          </Typography>
          <Typography
            variant="body2"
            sx={{ textAlign: 'center', color: '#64748b', mb: 4,  lineHeight: 1.5 }}
          >
            Please enter your details to access your device manager.
          </Typography>

          <form onSubmit={handleSubmit} noValidate>
            {/* Username Field */}
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 500, color: '#475569',  }}>
              Email Address
            </Typography>
            <LabeledField
              inputRef={usernameRef}
              placeholder="name@company.com"
              name="username"
              autoComplete="username"
              value={credentials.username}
              onChange={handleChange('username')}
              required
              error={Boolean(validationErrors.username)}
              helperText={validationErrors.username}
              disabled={loading}
              sx={{ mb: 3 }}
              textFieldSx={{
                '& .MuiOutlinedInput-root': {
                  height: 38,
                  backgroundColor: '#fafafa',
                  borderRadius: '10px',
                  '& fieldset': {
                    borderColor: '#e2e8f0',
                    borderWidth: '1px',
                  },
                  '&:hover fieldset': {
                    borderColor: '#cbd5e1',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: theme.palette.primary.main,
                    borderWidth: '1px',
                  },
                },
                '& .MuiOutlinedInput-input': {
                  padding: '12px 14px',
                  
                  color: '#334155',
                  '&:-webkit-autofill': {
                    WebkitBoxShadow: '0 0 0 100px #fafafa inset !important',
                    WebkitTextFillColor: '#334155 !important',
                  }
                }
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <MailOutlineIcon sx={{ color: '#94a3b8',  }} />
                  </InputAdornment>
                ),
              }}
              inputProps={{
                'aria-label': 'Username or email',
                'aria-required': 'true',
                'aria-invalid': Boolean(validationErrors.username),
              }}
            />
            
            {/* Password Field */}
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 500, color: '#475569',  }}>
              Password
            </Typography>
            <LabeledField
              placeholder="........"
              name="password"
              type={showPassword ? 'text' : 'password'}
              autoComplete="current-password"
              value={credentials.password}
              onChange={handleChange('password')}
              required
              error={Boolean(validationErrors.password)}
              helperText={validationErrors.password}
              disabled={loading}
              sx={{ mb: 2 }}
              textFieldSx={{
                '& .MuiOutlinedInput-root': {
                  height: 38,
                  backgroundColor: '#fafafa',
                  borderRadius: '10px',
                  '& fieldset': {
                    borderColor: '#e2e8f0',
                    borderWidth: '1px',
                  },
                  '&:hover fieldset': {
                    borderColor: '#cbd5e1',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: theme.palette.primary.main,
                    borderWidth: '1px',
                  },
                },
                '& .MuiOutlinedInput-input': {
                  padding: '12px 14px',
                  
                  color: '#334155',
                  letterSpacing: credentials.password && !showPassword ? '3px' : 'normal',
                  '&:-webkit-autofill': {
                    WebkitBoxShadow: '0 0 0 100px #fafafa inset !important',
                    WebkitTextFillColor: '#334155 !important',
                  }
                }
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockOutlinedIcon sx={{ color: '#94a3b8',  }} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={handleTogglePassword}
                      edge="end"
                      tabIndex={-1}
                      size="small"
                      sx={{ color: '#94a3b8' }}
                    >
                      {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              inputProps={{
                'aria-label': 'Password',
                'aria-required': 'true',
                'aria-invalid': Boolean(validationErrors.password),
              }}
            />

            {/* Remember Me + Forgot */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 1, mb: 4 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                    name="rememberMe"
                    color="primary"
                    disabled={loading}
                    size="small"
                    sx={{
                      color: '#cbd5e1',
                      p: 1,
                      '&.Mui-checked': {
                        color: theme.palette.primary.main,
                      },
                    }}
                  />
                }
                label={
                  <Typography variant="body2" sx={{ color: '#64748b',  fontWeight: 400 }}>
                    Remember me
                  </Typography>
                }
                sx={{ ml: -1 }}
              />
              <Link
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  toast.info('Password reset functionality coming soon');
                }}
                sx={{
                  color: theme.palette.primary.main,
                  textDecoration: 'none',
                  fontWeight: 500,
                  
                  '&:hover': {
                    textDecoration: 'underline',
                  },
                }}
              >
                Forgot Password?
              </Link>
            </Box>

            {/* Submit Button */}
            <Button className="action-btn process-btn"
              fullWidth
              type="submit"
              variant="contained"
              color="primary"
              size="large"
              disableElevation
              disabled={loading}
              sx={{ 
                mb: 4, 
                py: 1.25,
                textTransform: 'none', 
                 
                fontWeight: 500,
                boxShadow: 'none',
              }}
            >
              {loading ? 'Signing in...' : 'Sign In'}
            </Button>

            {/* {allowOfflineLogin && (
              <Button className="action-btn close-btn"
                fullWidth
                type="button"
                variant="outlined"
                color="primary"
                size="large"
                disabled={loading}
                sx={{ mb: 4, py: 1.25, borderRadius: '8px', textTransform: 'none', fontWeight: 500 }}
                onClick={handleOfflineAccess}
              >
                Continue Without Backend
              </Button>
            )} */}

            {/* <Box sx={{ mt: 2, pt: 2, borderTop: `1px solid ${theme.palette.divider}`, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                Don't have an account?
              </Typography>
            </Box> */}
          </form>
        </CardContent>
      </Card>
    </Box>
  );
};

export default LoginPage;
